import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, ChevronLeft, ChevronRight } from '../../icons';
import {
  Avatar, Badge, Button, CalendarGrid, PageHeader, Pagination, SearchInput, Select,
  StatGroup, StatGroups, StatTile, StatTiles, Table, Tabs, Toolbar,
  type CalendarGridDay, type TableColumn,
} from '../../components/shared';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import {
  getTeamAttendance, downloadTeamAttendanceCsv,
  type TeamAttendanceDto, type TeamMemberAttendanceDto,
} from '../../api/hr-team';
import { formatWorkMinutes } from '../../lib/daily-work';
import {
  fetchAttendanceCalendar, type AttendanceCalendarDto,
} from '../../api/hr-attendance-calendar';
import { leaveTypeLabel, tripTypeLabel } from '../../lib/leave-type';
import { leaveTimeRangeText } from '../../lib/partial-leave-time';
import { saveBlob } from '../../lib/save-file';
import {
  formatSpan, matchesStateFilter, STATE_FILTER_LABEL, type AttendanceStateFilter,
} from '../../lib/team-attendance';
import { fetchAnomalies } from '../../api/hr-anomalies';
import './TeamAttendance.css';
// «사람별» 격자(2026-10-18 · web 레인 W10-23a · 렌즈 Few) — 지금 API 만 쓴다(그 파일 머리말)
import { PeopleMonthGrid } from './PeopleMonthGrid';
import { getAttendanceRange } from '../../api/hr-team';

/**
 * 근태 현황 — **달력** (2026-09-06 · 사용자 지시로 다시 그렸다).
 *
 * 설계: 시안 `artifact 0029d566` 「근태 현황 달력」 · 정본 C6 「달력 격자」 · D9-3 「요약 묶음」.
 *
 * ★★★ **달력에는 휴가 · 출장만 선다.** 출근 · 미출근은 **매일 · 모두에게** 있는 값이라 칸마다
 *    마흔 줄이 서고, 그러면 **정작 비는 날이 묻힌다**. 달력이 답하는 물음은 하나다 —
 *    「이번 달에 누가 자리를 비우나」. 하루의 값(출근 · 퇴근 시각 · 연장)은 **아래 표**가 맡는다.
 * ★★ **달력과 표는 한 화면의 위아래**다. 칸을 누르면 표가 그날로 바뀌고, 어느 날을 보고 있는지는
 *    **고른 칸**이 말한다.
 * ★★ 요약은 **세 묶음**(근무 상태 · 시간과 기록 이상 · 초과 근무) — 아홉 칸을 한 줄로 늘어놓으면
 *    어느 것이 어느 갈래인지 안 보인다(정본 D9-3 이 이 화면에서 나왔다).
 * ★ **휴가 종류를 적는다**(2026-09-06 사용자 결정). 종전에는 서버가 필드를 일부러 뺐다 —
 *   달력이 「누가 무슨 휴가로 비우나」를 묻게 되어 열었다. **사유는 여전히 안 온다.**
 * ★ 조회는 **기록에 남는다**. 그것도 화면이 말한다. 볼 권한이 없으면 403 이다.
 *
 * ── 종전에서 없어진 것 ────────────────────────────────────────────────
 * ① 「이번 주 · 이번 달」의 **사람별 집계 표**는 달력이 대신한다(서버 라우트는 그대로 살아 있다).
 * ② **오른쪽 「살펴볼 것」 칸**(마감하지 않은 날 · 기준이 아직 안 잡힌 것) — 2026-09-06 사용자
 *    지시로 걷어냈다. 시안에 없는 자리였고, 그 일은 이미 제 메뉴가 맡고 있다 —
 *    마감하지 않은 날은 **「이상 근태」**(요약의 「기록 이상」 타일이 그리로 보낸다) ·
 *    입사일과 근무제 배정은 **「입사일 관리」 · 「근무제」** 다. 한 화면이 남의 일까지 이고 있으면
 *    표가 좁아지고(열두 칸이다) 어디서 고치는지도 흐려진다.
 */

/** 한 쪽에 스무 줄 — 앱의 다른 표(워크스페이스 용량)와 같은 값이다. */
const PAGE_SIZE = 20;

const ERRORS: Record<string, string> = {
  FORBIDDEN: '근태 현황은 팀장과 회사 관리자만 볼 수 있습니다.',
  INVALID_DATE: '날짜 형식이 올바르지 않습니다.',
};

/** 달력이 무엇을 보고 있나. 표는 늘 **고른 하루**다. */
type CalendarPeriod = 'month' | 'week';

/**
 * 「지각」에 **얼마나** 늦었는지 붙인다 (2026-08-31 · 시안 E1 — 「지각 24분」).
 *
 * ★ 두 값(찍은 시각 · 그날 기준 시각)이 **이미 그 줄에 있다** — 서버에 더 물을 것이 없다.
 * ★ 기준이 없거나 시각이 이상하면 **그냥 「지각」**이다 — 지어낸 숫자를 붙이지 않는다.
 */
function lateText(checkIn: string | null, baseStart: string | null): string {
  const at = toMinutes(checkIn);
  const base = toMinutes(baseStart);
  if (at === null || base === null || at <= base) return '지각';
  const m = at - base;
  /*
    ★ 길이 표기는 **공용 `formatWorkMinutes`** 를 쓴다 (2026-09-26). 앞의 「지각」은 그대로 둔다 —
      그것은 길이가 아니라 **판정**이고, 기준이 없으면 위에서 숫자 없이 「지각」으로 끝난다.
    ⚠ 이 갈아타기로 **두 자리가 바뀐다**: 60분 「지각 1시간 0분」 → 「지각 1시간」,
      120분도 같다. 잃는 것이 아니라 **어색한 것이 고쳐지는 쪽**이라 받아들였다(관제 결정).
  */
  return `지각 ${formatWorkMinutes(m)}`;
}

/** `HH:MM` → 분. 형식이 아니면 null(모르면 안 쓴다). */
function toMinutes(hhmm: string | null): number | null {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토'];

function todayIso(): string {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}

/** `YYYY-MM-DD` 를 하루 단위로 옮긴다. 달·주 이동은 서버가 준 구간으로 한다. */
function shiftIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return new Date(d.getTime() + days * 86_400_000).toISOString().slice(0, 10);
}

/** 「2026. 09」 · 「09.07 ~ 09.13」 — 지금 보고 있는 구간을 사람 말로. */
function periodLabel(period: CalendarPeriod, cal: AttendanceCalendarDto | null, cursor: string): string {
  if (period === 'month') {
    const [y, m] = cursor.split('-');
    return `${y}. ${m}`;
  }
  if (!cal) return cursor;
  return `${cal.from.slice(5).replace('-', '.')} ~ ${cal.to.slice(5).replace('-', '.')}`;
}

/** 「2026-09-09 수요일」 — 표가 어느 날을 그리는지. */
function dayLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${iso} ${WEEKDAY[d.getDay()]}요일`;
}

/** 분 → 「1:30」. 0 이면 「0:00」(모르는 것과 다르다), 없으면 「—」. */
function span(minutes: number | undefined): string {
  if (minutes === undefined) return '—';
  return formatSpan(minutes);
}

/**
 * ★★★ **한 화면, 두 자격** (2026-08-31 · 사용자 결정).
 *      · `lead`    = 맡은 조직. **내 조직만.** 전사 절을 아예 안 부른다.
 *      · `company` = 근태 관리. 전사 요약 · 이상 · 기준 미비까지 담당자의 것.
 */
export interface TeamAttendanceProps {
  /** 기본은 좁은 쪽 — 자격을 모르면 넓게 열지 않는다. */
  mode?: 'lead' | 'company';
}

export function TeamAttendancePage({ mode = 'lead' }: TeamAttendanceProps = {}): JSX.Element {
  const isCompanyView = mode === 'company';
  const [data, setData] = useState<TeamAttendanceDto | null>(null);
  const [cal, setCal] = useState<AttendanceCalendarDto | null>(null);
  /** 표가 그리는 **고른 하루**. */
  const [date, setDate] = useState(todayIso);
  /** 달력이 보고 있는 구간의 아무 날 — 달·주를 넘길 때 이것이 움직인다. */
  const [cursor, setCursor] = useState(todayIso);
  const [period, setPeriod] = useState<CalendarPeriod>('month');
  // «사람별»(W10-23a) — company 모드에만 있고 그때의 기본이다. 달 데이터(period 'month')를 그대로 쓴다.
  //   lead 모드(팀 근태)는 종전 그대로 월간 · 주간뿐이다.
  const [peopleView, setPeopleView] = useState(isCompanyView);
  // 줄 끝 «이 달 지각 · 퇴근 누락» — getAttendanceRange(month)의 lateDays · openDays. 못 읽으면 null(«—»).
  const [rangeTotals, setRangeTotals] = useState<ReadonlyMap<string, { late: number; open: number }> | null>(null);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [scope, setScope] = useState<'company' | 'team'>(isCompanyView ? 'company' : 'team');
  const [query, setQuery] = useState('');
  const [anomalyTotal, setAnomalyTotal] = useState<number | null>(null);
  /** 표의 쪽 — 거르개 · 날짜가 바뀌면 첫 쪽으로 돌아간다(아래 `useEffect`). */
  const [page, setPage] = useState(1);
  /** 요약 칸으로 거르기 — **칸이 곧 필터다.** 다시 누르면 풀린다. */
  const [stateFilter, setStateFilter] = useState<AttendanceStateFilter | null>(null);
  const [saving, setSaving] = useState(false);
  const [narrow, setNarrow] = useState<{ division: string | null; team: string | null }>(
    { division: null, team: null },
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** 표 — 고른 하루. 범위 판정은 종전 그대로(전사부터, 안 되면 팀). */
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let res = null as Awaited<ReturnType<typeof getTeamAttendance>> | null;
      if (scope === 'company') {
        try {
          res = await getTeamAttendance({ date, scope: 'company' });
        } catch {
          setScope('team');
        }
      }
      if (!res) res = await getTeamAttendance({ date, ...(teamId ? { teamId } : {}) });
      /**
       * ★★ **범위는 «달라고 한 것» 이 아니라 «받은 것» 으로 정한다.**
       *    옛 서버는 `scope` 를 모르고 종전대로 한 팀을 준다.
       */
      if (res.team) setScope('team');
      setData(res);
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      setError(code ? ERRORS[code] ?? '근태 현황을 불러오지 못했습니다.' : '근태 현황을 불러오지 못했습니다.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [date, teamId, scope]);

  useEffect(() => { void load(); }, [load]);

  /**
   * 달력 — 구간이 바뀔 때만 다시 받는다(날을 고르는 것으로는 안 받는다).
   * ★ 옛 서버에는 이 라우트가 없다(404) — 그때는 달력이 통째로 사라지고 표만 남는다.
   */
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await fetchAttendanceCalendar({
          period, date: cursor,
          ...(scope === 'company' ? { scope: 'company' as const } : {}),
          ...(teamId ? { teamId } : {}),
        });
        if (alive) setCal(res);
      } catch {
        if (alive) setCal(null);
      }
    })();
    return () => { alive = false; };
  }, [period, cursor, scope, teamId]);

  /**
   * «사람별» 줄 끝의 이 달 합계(W10-23a) — 볼 때만 묻는다. 못 읽으면(옛 서버 · 권한) «—» 로 둔다(0 으로 지어내지 않는다).
   */
  useEffect(() => {
    if (!isCompanyView || !peopleView) return;
    let alive = true;
    void (async () => {
      try {
        const res = await getAttendanceRange({
          period: 'month', date: cursor,
          ...(scope === 'company' ? { scope: 'company' as const } : {}),
          ...(teamId ? { teamId } : {}),
        });
        if (alive) setRangeTotals(new Map(res.members.map((m) => [m.userId, { late: m.lateDays, open: m.openDays }])));
      } catch {
        if (alive) setRangeTotals(null);
      }
    })();
    return () => { alive = false; };
  }, [isCompanyView, peopleView, cursor, scope, teamId]);

  /**
   * 「기록 이상(누계)」 타일의 숫자 — **되면 그린다**(팀장은 403 이라 그 칸이 아예 안 생긴다).
   * ★ 옛 서버는 `counts` 를 안 준다 — 없으면 «모른다»로 두고 **0 으로 지어내지 않는다**.
   */
  useEffect(() => {
    if (!isCompanyView) return;
    let alive = true;
    void (async () => {
      try {
        const res = await fetchAnomalies();
        if (alive) setAnomalyTotal(res.counts?.total ?? null);
      } catch {
        if (alive) setAnomalyTotal(null);
      }
    })();
    return () => { alive = false; };
  }, [isCompanyView]);

  const divisionNames = useMemo(
    () => [...new Set((data?.members ?? []).map((m) => m.divisionName).filter((v): v is string => !!v))].sort(),
    [data],
  );
  const teamNames = useMemo(() => [...new Set(
    (data?.members ?? [])
      .filter((m) => !narrow.division || m.divisionName === narrow.division)
      .map((m) => m.teamName).filter((v): v is string => !!v),
  )].sort(), [data, narrow.division]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    // ★ 찾기는 **이름 · 소속 · 사번**을 본다 — 사번으로 못 찾으면 표의 첫 칸이 헛것이 된다.
    const hit = (m: TeamMemberAttendanceDto) => (
      !q || `${m.name} ${m.teamName ?? ''} ${m.divisionName ?? ''} ${m.employeeNumber ?? ''}`
        .toLowerCase().includes(q)
    );
    const all = (data?.members ?? []).filter((m) => matchesStateFilter(m, stateFilter)).filter(hit);
    if (data?.team) return all;
    return all.filter((m) => (
      (!narrow.division || m.divisionName === narrow.division)
      && (!narrow.team || m.teamName === narrow.team)
    ));
  }, [data, narrow, stateFilter, query]);

  const gridPeople = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.members ?? []).filter((m) => (
      (!q || `${m.name} ${m.teamName ?? ''} ${m.divisionName ?? ''} ${m.employeeNumber ?? ''}`.toLowerCase().includes(q))
      && (data?.team || ((!narrow.division || m.divisionName === narrow.division) && (!narrow.team || m.teamName === narrow.team)))
    )).map((m) => ({ userId: m.userId, name: m.name, teamName: m.teamName ?? null }));
  }, [data, narrow, query]);
  // 오늘 칸의 «아직 출근 기록 없음» — 표가 오늘을 보고 있을 때만(그날 근태가 곧 오늘이다)
  const absentToday = useMemo(() => new Set(
    data && data.date === todayIso() ? data.members.filter((m) => m.state === 'ABSENT').map((m) => m.userId) : [],
  ), [data]);

  /**
   * ★★ **거르개 · 날짜 · 범위가 바뀌면 첫 쪽으로.** 안 그러면 「3쪽을 보다 거르니 빈 화면」이
   *    되고, 그것을 「사람이 없다」로 읽는다.
   */
  useEffect(() => { setPage(1); }, [date, stateFilter, query, narrow.division, narrow.team, teamId, scope]);

  const pageCount = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const curPage = Math.min(page, pageCount);
  const paged = useMemo(
    () => shown.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE),
    [shown, curPage],
  );

  const toggle = (f: AttendanceStateFilter) => setStateFilter((cur) => (cur === f ? null : f));

  /**
   * 달 · 주 넘기기 — **고른 날도 함께 옮긴다.**
   * ★ 커서만 옮기면 고른 칸이 화면 밖에 남아 「지금 어느 날을 보고 있나」가 사라진다.
   */
  const go = (dir: -1 | 1) => {
    if (period === 'week') {
      const next = shiftIso(cursor, dir * 7);
      setCursor(next);
      setDate(next);
      return;
    }
    const [y, m] = cursor.split('-').map(Number);
    const nd = new Date(Date.UTC(y as number, (m as number) - 1 + dir, 1));
    const next = nd.toISOString().slice(0, 10);
    setCursor(next);
    setDate(next);
  };
  const goToday = () => { const t = todayIso(); setCursor(t); setDate(t); };

  /** 달력 칸 — 서버가 준 날짜별 목록을 정본 `CalendarGrid` 의 모양으로 옮긴다. */
  const calendarDays: CalendarGridDay[] = useMemo(() => (cal?.days ?? []).map((d) => ({
    date: d.date,
    inMonth: d.inMonth,
    holiday: d.holiday,
    items: d.items.map((it) => ({
      id: `${it.kind}-${it.userId}`,
      label: `${it.name} ${it.kind === 'LEAVE' ? leaveTypeLabel(it.type) : tripTypeLabel(it.type)}${it.kind === 'LEAVE' && leaveTimeRangeText(it) ? ` ${leaveTimeRangeText(it)}` : ''}`,
      // 휴가는 info · 출장은 success — 「없다」와 「나가 있다」는 다른 일이다.
      tone: it.kind === 'LEAVE' ? ('info' as const) : ('success' as const),
    })),
  })), [cal]);

  const onDownload = async () => {
    setSaving(true);
    try {
      const { blob, filename } = await downloadTeamAttendanceCsv({
        date, ...(data?.team ? { teamId: data.team.id } : { scope: 'company' as const }),
      });
      saveBlob(blob, filename);
    } catch {
      setError('내려받지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * 표 열두 칸 (2026-09-06 · 사용자 지시).
   *
   * ★★ **출근 기록이 없는 두 경우**(휴가 · 간주)는 출근 · 퇴근 두 칸을 먹고 **왜 비었는지**를
   *    적는다 — 셋을 그냥 비우면 「기록이 없는 것」과 구별되지 않는다(정본 E1 `colSpan`).
   * ★ 「휴일대체」는 **쓴 대체휴가**가 아니라 **아직 대체로 안 바꾼 휴일근로**다. 쓴 것은
   *   「휴가」 칸에 「대체휴가」로 선다.
   */
  const memberColumns: ReadonlyArray<TableColumn<TeamMemberAttendanceDto>> = useMemo(() => [
    {
      key: 'empno',
      header: '사번',
      width: '92px',
      render: (m) => <span className="tmatt__num tmatt__mut">{m.employeeNumber ?? '—'}</span>,
    },
    {
      key: 'name',
      header: '사원명',
      width: '164px',
      rowHeader: true,
      render: (m) => (
        <span className="tmatt__who">
          <Avatar userId={Number(m.userId)} name={m.name} src={m.avatarUrl ?? undefined} size="sm" />
          <b className="tmatt__name">{m.name}</b>
          {m.isLeader && <span className="tmatt__tag">팀장</span>}
        </span>
      ),
    },
    {
      key: 'org',
      header: '부서명',
      width: '176px',
      render: (m) => (
        <span className="tmatt__mut">
          {[m.divisionName, m.teamName].filter(Boolean).join(' · ') || (
            <span className="tmatt__unassigned">회사 직속</span>
          )}
        </span>
      ),
    },
    {
      key: 'schedule',
      header: '근무제',
      width: '124px',
      render: (m) => <span className="tmatt__mut">{m.scheduleName ?? '—'}</span>,
    },
    {
      key: 'in',
      header: '출근',
      width: '88px',
      colSpan: (m) => (!m.checkIn && (m.state === 'DEEMED' || m.state === 'LEAVE') ? 2 : 1),
      render: (m) => {
        if (!m.checkIn && (m.state === 'DEEMED' || m.state === 'LEAVE')) {
          return (
            <span className="tmatt__why">
              {m.state === 'DEEMED'
                ? `${tripTypeLabel(m.deemedKind) ?? '출장'}으로 간주 · 기준 ${m.baseStart}`
                /* S6 — 반차 · 반반차에 구간이 있으면 「· 종일」 대신 구간(옛 기록 · 종일 휴가는 종전 그대로). */
                : `${leaveTypeLabel(m.leaveType) ?? '휴가'} · ${leaveTimeRangeText({ startTime: m.leaveStartTime, endTime: m.leaveEndTime }) ?? '종일'}`}
            </span>
          );
        }
        return <span className={m.late ? 'tmatt__num tmatt__lateNum' : 'tmatt__num'}>{m.checkIn ?? '—'}</span>;
      },
    },
    {
      key: 'out',
      header: '퇴근',
      width: '88px',
      render: (m) => <span className="tmatt__num tmatt__mut">{m.checkIn ? (m.checkOut ?? '—') : '—'}</span>,
    },
    {
      key: 'otApproved',
      header: '승인 초과',
      width: '96px',
      align: 'right',
      render: (m) => <span className="tmatt__num tmatt__mut">{span(m.overtimeApprovedMinutes)}</span>,
    },
    {
      key: 'otPending',
      header: '미승인 초과',
      width: '104px',
      align: 'right',
      // 결재가 밀린 것 — 결재자의 일이라 눈에 걸리게.
      render: (m) => (
        <span className={m.overtimePendingMinutes ? 'tmatt__num tmatt__warnNum' : 'tmatt__num tmatt__mut'}>
          {m.overtimePendingMinutes ? span(m.overtimePendingMinutes) : '—'}
        </span>
      ),
    },
    {
      key: 'otUnclaimed',
      header: '미신청 초과',
      width: '104px',
      align: 'right',
      render: (m) => (
        <span className={m.overtimeUnclaimedMinutes ? 'tmatt__num tmatt__warnNum' : 'tmatt__num tmatt__mut'}>
          {m.overtimeUnclaimedMinutes ? span(m.overtimeUnclaimedMinutes) : '—'}
        </span>
      ),
    },
    {
      key: 'leave',
      header: '휴가',
      width: '108px',
      render: (m) => (m.leaveType
        ? <Badge variant="info" size="sm">{leaveTypeLabel(m.leaveType)}</Badge>
        : <span className="tmatt__mut">—</span>),
    },
    {
      key: 'holidayWork',
      header: '보상휴가',
      width: '148px',
      /**
       * ★★★ **칸이 「무엇을 해야 하는지」를 가리킨다** (2026-09-06 · 판 C).
       *
       *    종전에는 「휴일근로 6시간」 하나뿐이라 **이미 냈는지 · 낼 수 있는지 · 바꿨는지**가
       *    다 같은 모양이었다. 이제 상태가 앞에 서고, 회사가 이 제도를 안 쓰면(`OFF`·옛 서버)
       *    **종전 그대로** 보인다 — 회귀 0.
       * ★ 「낼 수 있다」는 **승인된 연장 · 휴일근로**가 있을 때만이다. 승인 없이 머문 시간은
       *   여전히 「휴일근로 N시간」으로만 보인다(「승인된 만큼만 인정」).
       */
      render: (m) => {
        if (m.substituteState === 'APPROVED') {
          return <Badge variant="info" size="sm">{`대체 ${span(m.substituteMinutes)}`}</Badge>;
        }
        if (m.substituteState === 'PENDING') {
          return <Badge variant="warning" size="sm">{`신청 중 ${span(m.substituteMinutes)}`}</Badge>;
        }
        if (m.substituteState === 'ELIGIBLE') {
          return <Badge variant="success" size="sm">{`신청 가능 ${span(m.substituteMinutes)}`}</Badge>;
        }
        return m.holidayWorkMinutes
          ? <Badge variant="success" size="sm">{`휴일근로 ${span(m.holidayWorkMinutes)}`}</Badge>
          : <span className="tmatt__mut">—</span>;
      },
    },
    {
      key: 'anomaly',
      header: '근태 이상',
      render: (m) => {
        if (m.late) return <Badge variant="warning" size="sm">{lateText(m.checkIn, m.baseStart)}</Badge>;
        if (m.earlyLeave) return <Badge variant="warning" size="sm">조퇴</Badge>;
        if (m.checkIn && !m.checkOut && m.state !== 'WORKING') {
          return <Badge variant="danger" size="sm">퇴근 누락</Badge>;
        }
        if (m.state === 'ABSENT') return <Badge variant="danger" size="sm">미출근</Badge>;
        return <span className="tmatt__mut">—</span>;
      },
    },
  ], []);

  const s = data?.summary;

  return (
    <div className="tmatt">
      <PageHeader
        breadcrumb={crumbsFor(isCompanyView ? '/erp/attendance/company' : '/erp/attendance/team')}
        scope={scopeFor(isCompanyView ? '/erp/attendance/company' : '/erp/attendance/team')}
        title="근태 현황"
        description={!data
          ? (isCompanyView ? '경영 · 인사 담당자만 볼 수 있습니다' : '대표 · 부서장 · 팀장만 볼 수 있습니다')
          : data.team ? (
            <>
              {data.team.divisionName ? `${data.team.divisionName} · ` : ''}{data.team.name}
              {data.team.isLeader ? ' · 팀장' : ' · 회사 관리자'} · 팀원 {data.summary.total}명
            </>
          ) : <>전사 {data.summary.total}명</>}
        actions={(
          <Button
            variant="secondary"
            onClick={() => { void onDownload(); }}
            disabled={!data || saving}
          >
            {saving ? '받는 중…' : '내려받기'}
          </Button>
        )}
        help={{
          title: '이 화면의 규칙',
          body: (
            <>
              <p><b>달력에는 휴가 · 출장만 섭니다.</b> 출근 · 미출근은 매일 · 모두에게 있는 값이라
                달력을 채우면 정작 <b>비는 날</b>이 묻힙니다 — 하루의 값은 아래 표가 맡습니다.</p>
              <p><b>승인 · 반려는 전자결재에서 합니다</b> — 근태 화면은 무엇이 있었는지만 보여 줍니다.</p>
              {/*
                ★★★ **결근 설명은 여기 없다** (2026-09-11 · A-2).

                   2026-09-11(판 2-b)에 한 번 여기 적었다가 **옮겼다.** 이 화면은 달력 + 하루
                   표라 **결근을 한 번도 보여 주지 않는다** — 설명만 있으면 「어딘가에 있나 보다」
                   하고 찾게 만든다. 설명은 실제로 세어 보여 주는
                   **「우리 조직 › 이번 달 근무」**가 갖는다.
                ★ 되살리지 말 것. 여기에 결근을 «보여 주게» 된다면 그때 설명도 함께 온다.
              */}
              <p><b>초과근로는 셋으로 나뉩니다.</b> <b>승인</b>은 결재가 끝난 것, <b>미승인</b>은 신청은
                했으나 결재가 대기 · 반려인 것, <b>미신청</b>은 소정을 넘겨 남았는데 신청 자체가 없는
                것입니다 — 손을 쓰는 사람이 서로 다릅니다.</p>
              <p><b>「보상휴가」</b>는 연장 · 야간 · 휴일근로를 수당 대신 휴가로 받는 제도입니다
                (근로기준법 §57). 칸은 <b>「신청 가능」 → 「신청 중」 → 「대체」</b> 로 바뀌고,
                실제로 쓴 보상휴가는 「휴가」 칸에 섭니다.
                <br />· <b>「신청 가능」은 승인된 연장 · 휴일근로가 있을 때</b>만 뜹니다 — 신청은
                본인이 <b>「내 근태」의 「보상휴가」</b>에서 냅니다.
                <br />· <b>「휴일근로 N시간」</b>만 뜬다면 그날 <b>승인된 신청이 없다</b>는 뜻입니다
                (이 시스템은 승인된 만큼만 인정합니다).
                <br />· 회사가 이 제도를 <b>안 쓰면</b> 종전대로 휴일근로 시간만 보이고, 부여는
                <b>연차 관리</b>에서 담당자가 손으로 넣습니다. 켜는 자리는 <b>근태 관리 › 보상휴가</b>입니다.</p>
              {/*
                ★★ 개인정보 안내는 **도움말로 내렸다** (2026-09-06 사용자 지시).
                   본문 맨 위에 늘 떠 있던 넉 줄인데, 매일 여는 사람에게는 읽히지 않으면서
                   화면만 밀어 내렸다. **지우지 않고 옮긴다** — 「무엇이 보이고 무엇이 안 보이나」와
                   「조회가 남는다」는 이 화면이 반드시 말해야 하는 것이다.
              */}
              <p><b>근태 조회는 기록에 남습니다.</b> 이 화면에는 <b>시각 · 시간 · 휴가 종류</b>가
                표시되며, 휴가 <b>사유</b>와 정정 사유는 결재 문서 안에서만 볼 수 있습니다.</p>
              <p><b>내려받으면 그것도 기록에 남습니다</b> — 파일에는 <b>지금 범위의 전부</b>가
                담깁니다(화면에서 건 거르개는 반영되지 않습니다).</p>
            </>
          ),
        }}
      />

      {/*
        도구줄 — 왼쪽은 «무엇을 보는가»(구간 · 이동 · 범위), 오른쪽 «무엇을 하는가»는 머리로 갔다.
      */}
      <Toolbar className="tmatt__bar">
        <Tabs
          variant="pill"
          ariaLabel="구간"
          items={[
            ...(isCompanyView ? [{ value: 'people', label: '사람별' }] : []),
            { value: 'month', label: '월간' }, { value: 'week', label: '주간' },
          ]}
          value={peopleView ? 'people' : period}
          onChange={(v) => {
            setStateFilter(null);
            if (v === 'people') { setPeopleView(true); setPeriod('month'); return; }
            setPeopleView(false);
            setPeriod(v as CalendarPeriod);
          }}
        />
        <span className="tmatt__nav">
          <button type="button" aria-label={period === 'month' ? '지난 달' : '지난 주'} onClick={() => go(-1)}>
            <ChevronLeft size={16} aria-hidden />
          </button>
          <b className="tmatt__navlabel">{periodLabel(period, cal, cursor)}</b>
          <button type="button" aria-label={period === 'month' ? '다음 달' : '다음 주'} onClick={() => go(1)}>
            <ChevronRight size={16} aria-hidden />
          </button>
        </span>
        <Button variant="ghost" size="sm" onClick={goToday}>오늘</Button>
        {data && !data.team && (
          <>
            <Select
              ariaLabel="부서"
              size="sm"
              className="tmatt__pick"
              value={narrow.division ?? ''}
              options={[{ value: '', label: '부서 전체' }, ...divisionNames.map((d) => ({ value: d, label: d }))]}
              onChange={(v) => setNarrow({ division: v || null, team: null })}
            />
            <Select
              ariaLabel="팀"
              size="sm"
              className="tmatt__pick"
              value={narrow.team ?? ''}
              options={[{ value: '', label: '팀 전체' }, ...teamNames.map((t) => ({ value: t, label: t }))]}
              onChange={(v) => setNarrow((n) => ({ ...n, team: v || null }))}
            />
          </>
        )}
        {data && data.team && data.teams.length > 1 && (
          <Select
            ariaLabel="팀"
            size="sm"
            className="tmatt__pick"
            value={data.team.id}
            options={data.teams.map((t) => ({ value: t.id, label: `${t.name}${t.isLeader ? ' (내 팀)' : ''}` }))}
            onChange={(v) => setTeamId(v)}
          />
        )}
      </Toolbar>

      {error && <p className="tmatt__error" role="alert"><AlertCircle size={14} aria-hidden />{error}</p>}
      {loading && !data && <p className="tmatt__state">불러오는 중…</p>}

      {/*
        요약 세 묶음 (정본 D9-3) — **고른 하루**의 것이다.
        ★★ 다섯 칸(근무 중 · 퇴근 · 휴가 · 출장 · 미출근)은 **겹치지 않고 합이 전체**다 —
           칸이 곧 거르개라 겹치면 「7명인데 눌렀더니 아홉 줄」이 된다.
      */}
      {/*
        요약 세 묶음 (정본 D9-3 · **알약 줄**) — **고른 하루**의 것이다.
        ★★ 다섯 칸(근무 중 · 퇴근 · 휴가 · 출장 · 미출근)은 **겹치지 않고 합이 전체**다 —
           칸이 곧 거르개라 겹치면 「1명인데 눌렀더니 아홉 줄」이 된다.
        ★★ **알약 줄**을 쓰는 까닭(2026-09-06 사용자 결정): 이 화면은 **달력이 주인공**이라
           요약이 낮아야 한다. 같은 아홉 칸을 타일로 그리면 354px, 알약 줄이면 119px 이다.
      */}
      {data && s && (
        <StatGroups inline className="tmatt__groups">
          <StatGroup title="근무 상태">
            <StatTiles>
              <StatTile
                size="chip" tone="primary" value={s.workingNow ?? s.working} unit="명" label="근무 중"
                ariaLabel="근무 중만 보기"
                onClick={() => toggle('WORKING_NOW')} selected={stateFilter === 'WORKING_NOW'}
              />
              <StatTile
                size="chip" value={s.off} unit="명" label="퇴근"
                ariaLabel="퇴근만 보기" onClick={() => toggle('OFF')} selected={stateFilter === 'OFF'}
              />
              <StatTile
                size="chip" value={s.onLeave} unit="명" label="휴가"
                ariaLabel="휴가만 보기" onClick={() => toggle('LEAVE')} selected={stateFilter === 'LEAVE'}
              />
              <StatTile
                size="chip" value={s.trip ?? null} unit="명" label="출장 · 외근"
                ariaLabel="출장 · 외근만 보기" onClick={() => toggle('TRIP')} selected={stateFilter === 'TRIP'}
              />
              <StatTile
                size="chip" tone="danger" value={s.absent} unit="명" label="미출근"
                ariaLabel="미출근만 보기" onClick={() => toggle('ABSENT')} selected={stateFilter === 'ABSENT'}
              />
            </StatTiles>
          </StatGroup>

          <StatGroup title="시간 · 기록 이상">
            <StatTiles>
              <StatTile
                size="chip" tone="warning" value={s.late ?? null} unit="명" label="지각"
                ariaLabel="지각만 보기" onClick={() => toggle('LATE')} selected={stateFilter === 'LATE'}
              />
              {/* 조퇴는 «걸러 볼» 자리가 없다 — 상태가 아니라 그날의 승인 건이다. */}
              <StatTile size="chip" value={s.earlyLeave ?? null} unit="명" label="조퇴" />
              {/*
                ★ 「기록 이상」은 **누계**라 이 표에서 거를 수 없다 — 거르는 시늉을 하느니
                  이상 근태로 보낸다(정본 D9-2 「가는 타일」).
              */}
              {anomalyTotal !== null && (
                <StatTile
                  size="chip" tone="danger" value={anomalyTotal} unit="명" label="기록 이상 (누계)"
                  to="/erp/attendance/anomalies"
                />
              )}
            </StatTiles>
          </StatGroup>

          <StatGroup title="초과 근무">
            <StatTiles>
              {/*
                ★★ 알약은 곁말(`note`)을 안 그린다. 시간 합계는 **단위 뒤에** 붙인다 —
                   「승인 86시간 12명」처럼 라벨에 넣으면 **어느 숫자가 그 알약의 값인지** 흐려진다
                   (그렇게 그려 보고 고쳤다). 이름 → 숫자 → 「명 · 86시간」 순서라야 읽힌다.
              */}
              <StatTile
                size="chip" label="승인" value={s.overtimeApproved?.users ?? null}
                unit={s.overtimeApproved ? `명 · ${span(s.overtimeApproved.minutes)}` : '명'}
              />
              <StatTile
                size="chip" tone="warning" label="미승인" value={s.overtimePending?.users ?? null}
                unit={s.overtimePending ? `명 · ${span(s.overtimePending.minutes)}` : '명'}
              />
              <StatTile
                size="chip" tone="warning" label="미신청" value={s.overtimeUnclaimed?.users ?? null}
                unit={s.overtimeUnclaimed ? `명 · ${span(s.overtimeUnclaimed.minutes)}` : '명'}
              />
            </StatTiles>
          </StatGroup>
        </StatGroups>
      )}

      {/*
        ★ 팀 범위에서만 적는다 — 전사에서는 이 합이 「우리 팀」이 아니라 회사 전체라 뜻이 달라진다.
          종전 화면이 말하던 것이고, 요약 묶음에는 자리가 없어 여기 한 줄로 남긴다.
      */}
      {data && s && data.team && (
        <p className="tmatt__note">
          팀 실근로 <b>{formatSpan(s.creditedMinutes)}</b> / 소정 {formatSpan(s.scheduledMinutes)} —
          인정 근로는 사람마다의 소정을 넘지 않습니다.
        </p>
      )}

      {/*
        달력 — **휴가 · 출장만** 선다. 칸을 누르면 아래 표가 그날로 바뀐다.
        ★ 옛 서버에는 이 라우트가 없다(404) — 그때는 이 절이 통째로 사라지고 표만 남는다.
      */}
      {cal && peopleView && (
        <PeopleMonthGrid
          caption={`사람별 근태 · ${periodLabel('month', cal, cursor)}`}
          days={cal.days.filter((d) => d.inMonth)}
          people={gridPeople}
          totals={rangeTotals}
          today={todayIso()}
          absentToday={absentToday}
          selected={date}
          onSelect={(d) => setDate(d)}
        />
      )}
      {cal && !peopleView && (
        <CalendarGrid
          className="tmatt__cal"
          ariaLabel={period === 'month' ? '월간 근태 달력' : '주간 근태 달력'}
          days={calendarDays}
          week={period === 'week'}
          selected={date}
          today={todayIso()}
          onSelect={(d) => setDate(d)}
        />
      )}

      {/* 옆 칸이 없어졌으므로 상자도 없앤다 — 한 칸짜리 격자는 격자가 아니다. */}
      {data && s && (
        <section className="tmatt__tablewrap" aria-label={data.team ? '팀원' : '전 직원'}>
          <div className="tmatt__tabletop">
            <b>{dayLabel(date)}</b>
            <span className="tmatt__count">{shown.length}명</span>
            <span className="tmatt__sp" />
            <SearchInput
              aria-label="이름 · 부서 · 사번으로 찾기"
              placeholder="이름 · 부서 · 사번"
              size="sm"
              className="tmatt__search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClear={() => setQuery('')}
            />
          </div>
          <Table
            caption={`${dayLabel(date)} 근태`}
            columns={memberColumns}
            rows={paged}
            rowKey={(m) => m.userId}
            empty={data.members.length === 0
              ? <p className="tmatt__state">{data.team ? '이 팀에는 팀원이 없습니다.' : '재직 중인 사람이 없습니다.'}</p>
              : (
                // ★ 「없다」와 「걸러서 없다」는 다른 말이다 — 무엇으로 걸렀는지도 적는다.
                <p className="tmatt__state">
                  {stateFilter
                    ? `«${STATE_FILTER_LABEL[stateFilter]}» 인 사람이 없습니다.`
                    : query.trim()
                      ? `«${query.trim()}» 으로 찾은 사람이 없습니다.`
                      : '고른 부서·팀에 해당하는 사람이 없습니다.'}
                </p>
              )}
          />
          {/*
            페이저 (정본 E4 · `offset`) — 서버가 **그날 전부**를 주므로 총 건수를 안다.
            ★ 정본의 규칙: 총 건수를 모르면 쪽 번호를 안 그린다. 여기서는 «안다».
            ★★ **셈 줄은 늘 세우고 쪽 단추만 접는다** — 한 쪽에 다 들어갈 때 줄까지 없애면
               「몇 명 중 몇을 보고 있나」가 사라져 **쪽이 아예 없는 표로 읽힌다**(그래서 고쳤다).
               누를 데 없는 단추는 여전히 안 세운다.
          */}
          {shown.length > 0 && (
            <div className="tmatt__foot">
              <span>
                전체 <b>{shown.length}</b>명 중{' '}
                <b>{(curPage - 1) * PAGE_SIZE + 1}–{Math.min(shown.length, curPage * PAGE_SIZE)}</b>
                {pageCount > 1 && <> · {curPage}/{pageCount}쪽</>}
              </span>
              {pageCount > 1 && (
                <Pagination
                  mode="offset"
                  ariaLabel="근태 목록 쪽"
                  page={curPage}
                  pageSize={PAGE_SIZE}
                  total={shown.length}
                  onPageChange={setPage}
                />
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
